/**
 * Page Loader Component
 *
 * Loading skeleton displayed while lazy-loaded pages are being fetched.
 * Used as the Suspense fallback for route-based code splitting.
 */

import { Loader2 } from 'lucide-react'

/**
 * Full-page loading skeleton with centered spinner
 *
 * Used as fallback for React.lazy() loaded pages.
 */
export function PageLoader() {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

/**
 * Full-screen page loader for top-level routes
 *
 * Used when the entire page content is lazy-loaded.
 */
export function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

/**
 * Skeleton loader for content areas
 *
 * Used for lazy-loaded components within a page.
 */
export function ContentLoader() {
  return (
    <div className="space-y-4 p-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-10 w-32 animate-pulse rounded bg-muted" />
      </div>

      {/* Content skeleton */}
      <div className="space-y-3">
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
      </div>

      {/* Card skeleton */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg border bg-muted" />
        ))}
      </div>
    </div>
  )
}

export default PageLoader
